import React, { Fragment } from 'react';
import Helmet from 'react-helmet';
import _ from 'lodash';
import { Box } from '@mui/material';
import { CloseEditView } from '../components/CloseEditView';
import {
  FormElement,
  FormListItem,
  FormList,
} from '../components/CommonFormComponents';
import { BranchListDropdown } from '../components/dropdowns/single/BranchList';
import { PrimaryCompanyNaviList } from '../components/list/PrimaryCompanyNaviList';
import { FormInformationDialog } from '../components/FormInformationDialog';
import { Information } from '../components/modals/common/';
import { MasterPageHeader } from '../components/PageHeader';
import { HEADER_CONSTANTS, STATUS_CODE } from '../utilities/constants';
import dialogLoader from '../utilities/dialogLoader';
import _masterSlideLoader from '../utilities/masterSlideLoader';
import { isLoginUserPrime } from '../utilities/roleUtility';
import { messageUtility } from '../utilities/messageUtility';
import { extractErrorMessage } from '../utilities/commonUtil';
import SessionStorageService from '../services/utilities/SessionStorageService';
import { CompanyBranchService } from '../services/CompanyBranches';
import CompanyService from '../services/Companies';
import { UserProfileService } from '../services/UserProfiles';
import { isNullOrUndefinedOrEmptyStr } from '../utilities/stringUtil';

const CONFIRM_ITEM = _.map(_.range(1, 11), id => ({
  id,
  label: `項目${id}`,
  valueKey: `confirm_item_${id}_title`,
  valueDropdownSettingKey: `confirm_item_${id}_value`,
}));

/**
 * @description 作業終了報告設定画面
 */
export class WorkFinishReportSetting extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      dataSettingInits: {},
      dataSettings: {},
      expirationCompany: {},
      isLoading: true,
      branch: {},
      dirtyFields: [],
      isLoginUserPrime: isLoginUserPrime(),
      userId: localStorage.getItem('user_id'),
      informationModal: {
        show: false,
        title: null,
        message: null,
        buttonText: null,
        multilineText: false,
      },
      validateMessage: {},
    };
    this.isSubmittingFlag = false;
  }

  getExpiration = () => {
    CompanyService.expirations(SessionStorageService.getCompanyId()).then(
      response => {
        if (!response.result) {
          return;
        }
        this.setState({
          expirationCompany: {
            ...this.state.expirationCompany,
            ...response.data.entities.company.expiration,
          },
        });
      }
    );
  };

  handleLoadDataSetting = async (branchId = this.state.branch.id) => {
    this.setState({ isLoading: true });
    const response = await CompanyBranchService.getKYWorkFinishReportItemSettings(
      branchId
    );
    let itemSettings = {};
    if (response.result) {
      itemSettings = response.data.entities.ky_work_finish_report_item_settings;
    }

    this.setState({
      dataSettingInits: itemSettings,
      dataSettings: itemSettings,
      isLoading: false,
      dirtyFields: [],
      validateMessage: {},
    });
  };

  setBranch = async (name, value) => {
    if (this.state.isLoading) {
      return;
    }

    if (!_.isEmpty(this.state.dirtyFields)) {
      dialogLoader.show(CloseEditView, {
        callback: {
          clear: () => {
            this.setState({ branch: { id: value, name } });
            this.handleLoadDataSetting(value);
            dialogLoader.close();
          },
          close: () => {
            dialogLoader.close();
          },
        },
      });
    } else {
      this.setState({ branch: { id: value, name } });
      this.handleLoadDataSetting(value);
    }
  };

  handleGetBranch = async () => {
    if (this.state.isLoginUserPrime) {
      const response = await CompanyBranchService.companyBranchList({
        use_ky_flag: 1,
        limit: 1,
      });
      if (
        response.result &&
        !_.isEmpty(response.data.entities.company_branches)
      ) {
        const [firstBranch] = response.data.entities.company_branches;
        this.setState({ branch: firstBranch });
        this.handleLoadDataSetting(firstBranch.id);
      }
    } else {
      UserProfileService.show(this.state.userId).then(response => {
        if (response.result) {
          const company_branch = response.data.entities.company_branch[0];
          this.setState({ branch: company_branch });
          this.handleLoadDataSetting(company_branch.id);
        }
      });
    }
  };

  componentDidMount = () => {
    this.getExpiration();
    this.handleGetBranch();
  };

  handleChangeDataSetting = (e, configKey) => {
    const { value } = e.target;
    this.setState(prevState => {
      let dataSettings = _.cloneDeep(prevState.dataSettings);
      dataSettings[configKey] = value;

      const isDirty =
        _.toString(prevState.dataSettingInits[configKey]) !== _.toString(value);
      const dirtyFields = isDirty
        ? _.uniq([...prevState.dirtyFields, configKey])
        : _.without(prevState.dirtyFields, configKey);

      return {
        dataSettings,
        dirtyFields,
      };
    });
  };

  showErrorMessageDialog = message => {
    this.setState({
      informationModal: {
        show: true,
        title: messageUtility.getMessage('E-001-402'),
        message: message,
        buttonText: messageUtility.getMessage('I-000-017'),
        multilineText: true,
      },
    });
  };

  closeDialog = () => {
    this.setState({
      informationModal: {
        show: false,
        title: null,
        message: null,
        buttonText: null,
        multilineText: false,
      },
    });
  };

  handleValidate = () => {
    const { dataSettings } = this.state;
    let newValidateMessage = {};
    _.forEach(CONFIRM_ITEM, ({ valueKey, valueDropdownSettingKey }) => {
      if (
        isNullOrUndefinedOrEmptyStr(dataSettings[valueKey]) &&
        !isNullOrUndefinedOrEmptyStr(dataSettings[valueDropdownSettingKey])
      ) {
        newValidateMessage[valueKey] = '必須です。';
      }
      if (
        isNullOrUndefinedOrEmptyStr(dataSettings[valueDropdownSettingKey]) &&
        !isNullOrUndefinedOrEmptyStr(dataSettings[valueKey])
      ) {
        newValidateMessage[valueDropdownSettingKey] = '必須です。';
      }
    });

    this.setState({ validateMessage: newValidateMessage });
    return _.isEmpty(newValidateMessage);
  };

  handleSubmitSetting = async () => {
    if (
      this.state.isLoading ||
      this.isSubmittingFlag ||
      !this.handleValidate()
    ) {
      return;
    }

    this.isSubmittingFlag = true;
    CompanyBranchService.updateWorkFinishReportSettings(
      this.state.branch.id,
      this.state.dataSettings
    )
      .then(response => {
        if (response.result) {
          this.handleLoadDataSetting();
          dialogLoader.show(FormInformationDialog, {
            title: '設定更新完了',
            message: '設定の更新が完了しました。',
            onClose: () => {
              dialogLoader.close();
            },
          });
        }
      })
      .catch(error => {
        if (error.status === STATUS_CODE.forbidden) {
          this.showErrorMessageDialog(messageUtility.getMessage('E-001-433'));
        } else if (error.status === STATUS_CODE.notFound) {
          this.showErrorMessageDialog(
            '選択中の支店には操作権限がありません。操作可能な支店を選択してください。'
          );
        } else {
          this.showErrorMessageDialog(extractErrorMessage(error));
        }
      })
      .finally(() => {
        this.isSubmittingFlag = false;
      });
  };

  /**
   * 戻るダイアログ表示
   */
  handleGoBackDialog = () => {
    dialogLoader.show(CloseEditView, {
      callback: {
        clear: () => {
          _masterSlideLoader.close();
          dialogLoader.close();
        },
        close: () => {
          dialogLoader.close();
        },
      },
    });
  };

  render() {
    const {
      isLoading,
      expirationCompany,
      dataSettings,
      branch,
      isLoginUserPrime,
      informationModal,
      validateMessage,
    } = this.state;
    return (
      <div id='slide-view' className='contruction-slide-view show'>
        <Helmet>
          <title>{HEADER_CONSTANTS.title} - 作業終了報告設定</title>
        </Helmet>
        <div className='contruction-slide-view__view'>
          <MasterPageHeader
            title={'会社情報'}
            onClose={() => this.handleGoBackDialog()}
          />
          <div className='contruction-slide-view__body contruction-slide-view__body--height-fix'>
            <div className='contruction-detail-view'>
              <div className='contruction-detail-view__sidemenu'>
                <PrimaryCompanyNaviList
                  isSelectedSettingWorkFinishReport={true}
                  expirationCompany={expirationCompany}
                  isEditable={true}
                />
              </div>
              <div className='contruction-detail-view__body contruction-detail-view__body--fixed'>
                <WorkFinishReportHeader
                  handleSubmitSetting={this.handleSubmitSetting}
                  branch={branch}
                  setBranch={this.setBranch}
                  isLoginUserPrime={isLoginUserPrime}
                />
                <div className='contruction-detail-view__bar' />
                <div className='contruction-detail-view__main--fixed'>
                  {isLoading ? (
                    <div className='indicator' />
                  ) : (
                    <WorkFinishReportSettingForm
                      dataSettings={dataSettings}
                      handleSetData={this.handleChangeDataSetting}
                      validateMessage={validateMessage}
                    />
                  )}
                </div>
                <Information
                  title={informationModal.title}
                  message={informationModal.message}
                  buttonText={informationModal.buttonText}
                  multilineText={informationModal.multilineText}
                  visible={informationModal.show}
                  onClose={() => this.closeDialog()}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const WorkFinishReportHeader = ({
  handleSubmitSetting,
  branch,
  setBranch,
  isLoginUserPrime,
}) => (
  <Box sx={{ padding: '10px 16px' }}>
    <div className='kiken-setting__pageHeader--title'>作業終了報告</div>
    <div className='contruction-detail-view__menu-dropdown kiken-setting__header'>
      <span className='construction-detail-view__button'>
        <button className='btn--effective' onClick={handleSubmitSetting}>
          更新
        </button>
      </span>
      <span className='construction-detail-view__button'>
        <div className='construction-detail-view__header-dropdown set-width-dropdown'>
          {isLoginUserPrime ? (
            <Fragment>
              <BranchListDropdown
                value={branch.id}
                branchName={branch.name}
                set={setBranch}
                useKYOnly={true}
              />
              <div className='construction-detail-view__dropdown-text'>
                <p className='construction-detail-view__header-label'>
                  支店を選択
                </p>
                <p className='label'>※支店ごとに情報が違います</p>
              </div>
            </Fragment>
          ) : (
            <div className='BranchListDropdown'>
              <div className='dropdown-single-select'>
                <span>{branch.name}</span>
              </div>
            </div>
          )}
        </div>
      </span>
    </div>
  </Box>
);

const renderSettingRow = (
  dataSettings,
  handleSetData,
  configItem,
  validateMessage
) => {
  return (
    <tr key={configItem.id} className='kiken-setting__row'>
      <td className='kiken-setting__row--column-title'>{configItem.label}</td>
      <td className='kiken-setting__row--column-input'>
        <FormList style={{ marginBottom: 0 }}>
          <FormListItem style={{ borderBottom: 'none' }}>
            <FormElement
              style={{ width: '100%' }}
              validate={validateMessage[configItem.valueKey]}
            >
              <input
                type='text'
                name='value'
                className='--col-xs-10 --col-md-10 --col-lg-10 form-list__input-large'
                value={dataSettings[configItem.valueKey] || ''}
                onChange={e => handleSetData(e, configItem.valueKey)}
                maxLength={36}
              />
            </FormElement>
          </FormListItem>
        </FormList>
      </td>
      <td className='kiken-setting__row--column-input'>
        <FormList style={{ marginBottom: 0 }}>
          <FormListItem style={{ borderBottom: 'none' }}>
            <FormElement
              style={{ width: '100%' }}
              validate={validateMessage[configItem.valueDropdownSettingKey]}
            >
              <input
                type='text'
                name='valueDropdown'
                className='--col-xs-10 --col-md-10 --col-lg-10 form-list__input-large'
                value={dataSettings[configItem.valueDropdownSettingKey] || ''}
                onChange={e =>
                  handleSetData(e, configItem.valueDropdownSettingKey)
                }
                maxLength={255}
                placeholder='プルダウンリストを入力（半角のカンマ区切り）'
              />
            </FormElement>
          </FormListItem>
        </FormList>
      </td>
      <td />
    </tr>
  );
};

const WorkFinishReportSettingForm = ({
  dataSettings,
  handleSetData,
  validateMessage,
}) => (
  <Fragment>
    <table className='table'>
      <thead>
        <tr className='table__label'>
          <td className='value-table kiken-setting__main-table-label'>
            <div className='value-table-label'>
              作業終了報告の確認事項設定（最大10項目）
            </div>
          </td>
        </tr>
        <tr>
          <td className='kiken-setting__table--description no-sub-table'>
            作業終了報告の報告項目（最大10項目）を設定できます。回答リスト（プルダウン）は半角のカンマ区切りで設定できます。
          </td>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className='kiken-setting__table--data'>
            <table className='table'>
              <tbody>
                {_.map(CONFIRM_ITEM, configItem =>
                  renderSettingRow(
                    dataSettings,
                    handleSetData,
                    configItem,
                    validateMessage
                  )
                )}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  </Fragment>
);



// WEBPACK FOOTER //
// ./src/pages/WorkFinishReportSetting.jsx